import { useState, useEffect, useCallback } from 'react';
import { PawPrint, X, ArrowRight } from 'lucide-react';
import { apiClient } from '@/api/axios';
import { useAddPetModal } from '../context/AddPetModalContext';

interface PetRegistrationNotification {
  id: string;
  petName: string;
  professionalName: string;
  metadata?: {
    businessName?: string;
    petSpecies?: string;
  };
  createdAt: string;
}

export function PetRegistrationBanner() {
  const [notifications, setNotifications] = useState<PetRegistrationNotification[]>([]);
  const addPetModal = useAddPetModal();

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/notifications', {
        params: { limit: 50 },
      });
      const items = data.notifications ?? data.items ?? [];
      const petRegs = items.filter(
        (n: any) => n.type === 'pet_registered' && !n.read && n.actionRequired,
      );
      setNotifications(petRegs);
    } catch {
      // Silently fail — banner is non-critical
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleDismiss = async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiClient.patch(`/notifications/${id}/read`);
    } catch {
      // best-effort
    }
  };

  const handleCompleteProfile = async (n: PetRegistrationNotification) => {
    // Open the Add Pet modal pre-filled with the pet's data
    if (addPetModal) {
      addPetModal.openAddPet({
        name: n.petName,
        species: n.metadata?.petSpecies || undefined,
      });
    }
    // Dismiss the notification
    setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    try {
      await apiClient.patch(`/notifications/${n.id}/read`);
    } catch {
      // best-effort
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="mx-4 sm:mx-6 md:mx-8 mt-3">
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800/40 flex items-center justify-center shrink-0">
            <PawPrint className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-200">
              {notifications.length === 1
                ? 'A professional registered your pet'
                : `${notifications.length} pets were registered by professionals`}
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              Complete their profile and set up a Digital ID (DID) on Petso
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {notifications.map((n) => (
            <li
              key={n.id}
              className="flex items-center justify-between gap-3 bg-white dark:bg-[#16151D] rounded-lg border border-purple-100 dark:border-purple-500/20 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">{n.petName}</span>
                  {n.metadata?.petSpecies && (
                    <span className="text-gray-500 dark:text-white/50"> · {n.metadata.petSpecies}</span>
                  )}
                </p>
                <p className="text-xs text-gray-600 dark:text-white/60 mt-0.5">
                  Registered by {n.professionalName || n.metadata?.businessName || 'a professional'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCompleteProfile(n); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg shadow-sm cursor-pointer"
                  style={{ backgroundColor: '#7c3aed', color: '#ffffff' }}
                >
                  <ArrowRight className="w-4 h-4" />
                  Complete Profile
                </button>
                <button
                  type="button"
                  onClick={() => handleDismiss(n.id)}
                  className="inline-flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
